import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Check, Crown } from 'lucide-react';

const MONTHLY_URL = 'https://varimesrdcom.sk/order-confirmation-monthly-624803';
const YEARLY_URL = 'https://varimesrdcom.sk/objednavka';

const PERKS = {
  sk: ['Prístup ku všetkým receptom', '13 ebookov zdarma', '6-dielny videokurz', 'Nové recepty každý mesiac'],
  cz: ['Přístup ke všem receptům', '13 ebooků zdarma', '6dílný videokurz', 'Nové recepty každý měsíc'],
};

// Reusable membership plan picker. Pass a clickable element as `trigger`.
export const MembershipDialog = ({ trigger }) => {
  const { language } = useAuth();
  const [open, setOpen] = useState(false);
  const sk = language === 'sk';
  const perks = sk ? PERKS.sk : PERKS.cz;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild data-testid="membership-dialog-trigger">{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="membership-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading">
            {sk ? 'Vyberte si členstvo' : 'Vyberte si členství'}
          </DialogTitle>
          <DialogDescription>
            {sk
              ? 'Plný prístup ku všetkým receptom, ebookom a videokurzu.'
              : 'Plný přístup ke všem receptům, ebookům a videokurzu.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4 pt-2">
          {/* Monthly */}
          <div
            className="rounded-2xl border border-border bg-white p-6 flex flex-col"
            data-testid="plan-monthly"
          >
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              {sk ? 'Mesačné' : 'Měsíční'}
            </p>
            <div className="mt-2 mb-1 flex items-baseline gap-1">
              <span className="text-4xl font-bold text-heading">9,90 €</span>
              <span className="text-muted-foreground text-sm">/ {sk ? 'mesiac' : 'měsíc'}</span>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              {sk ? 'Zrušíte kedykoľvek' : 'Zrušíte kdykoliv'}
            </p>
            <ul className="space-y-2 mb-6 flex-1">
              {perks.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
            <a href={MONTHLY_URL} target="_blank" rel="noopener noreferrer" data-testid="plan-monthly-link">
              <Button variant="outline" className="w-full rounded-full border-2">
                {sk ? 'Vybrať mesačné' : 'Vybrat měsíční'}
              </Button>
            </a>
          </div>

          {/* Yearly — highlighted */}
          <div
            className="relative rounded-2xl border-2 border-primary bg-primary/5 p-6 flex flex-col shadow-hover"
            data-testid="plan-yearly"
          >
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
              <Crown className="w-3.5 h-3.5" /> {sk ? 'Najvýhodnejšie' : 'Nejvýhodnější'}
            </span>
            <p className="text-sm font-medium text-primary uppercase tracking-wide">
              {sk ? 'Ročné' : 'Roční'}
            </p>
            <div className="mt-2 mb-1 flex items-baseline gap-2">
              <span className="text-4xl font-bold text-heading">47 €</span>
              <span className="text-lg text-muted-foreground line-through decoration-primary/60">108 €</span>
              <span className="text-muted-foreground text-sm">/ {sk ? 'rok' : 'rok'}</span>
            </div>
            <div className="inline-flex self-start items-center gap-1 bg-secondary text-white text-xs font-bold px-2.5 py-1 rounded-full mb-2 shadow-sm">
              {sk ? 'Ušetríte 61 € (−56 %)' : 'Ušetříte 61 € (−56 %)'}
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              {sk
                ? 'Len 3,92 €/mesiac — akcia platí teraz'
                : 'Jen 3,92 €/měsíc — akce platí nyní'}
            </p>
            <ul className="space-y-2 mb-6 flex-1">
              {perks.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
            <a href={YEARLY_URL} target="_blank" rel="noopener noreferrer" data-testid="plan-yearly-link">
              <Button className="w-full rounded-full bg-primary hover:bg-primary-hover text-white">
                {sk ? 'Vybrať ročné' : 'Vybrat roční'}
              </Button>
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
